<template>
  <div
    ref="elementRef"
    v-style:bg="!parallaxOffset ? backgroundColor : null"
    class="md:flex"
    :class="{
      'overflow-hidden': parallaxLayer,
      '<md:f-col <md:min-h-0 <md:overflow-hidden': usesPercentHeightSmMd,
      'lg:min-h-0 lg:children:h-inherit lg:o:h-$h-lg': usesPercentHeightLg,
      'md:min-h-0': usesPercentHeightLg,
      'o:h-$h': usesPercentHeightSmMd && usesPercentHeightLg,
      'o:<lg:h-$h': usesPercentHeightSmMd && !usesPercentHeightLg,
    }"
    :style="percentHeightStyle"
  >
    <div
      v-if="splitLayoutAlignmentLarge.startsWith('1') && parallaxOffset"
      class="relative flex shrink-0"
      :class="[
        mediaSize[imageWidth],
        {
          '<md:flex-1 <md:min-h-0 <md:overflow-hidden': usesPercentHeightSmMd,
          'lg:h-full lg:min-h-0 lg:overflow-hidden': usesPercentHeightLg,
        },
      ]"
    >
      <cms-base-caption v-if="hasCaption" v-bind="captionProps" />
      <div v-style="{ pb: contentPane }" class="grid cols-2 w-full md:flex">
        <div class="absolute-0 overflow-hidden" :class="roundedCornersClasses">
          <div ref="parallaxRef" class="relative" :style="{ height: `calc(100% + ${parallaxTranslate})` }">
            <base-picture
              v-if="responsiveMedia.images"
              v-bind="responsiveMedia.images"
              class="full"
              :fetchpriority
              fit="cover"
              :lazy
              :position="objectPosition"
            />
            <vf-video
              v-else-if="responsiveMedia.video"
              v-bind="responsiveMedia.video"
              ref="videoRefs"
              :autoplay="!lazy"
              class="w-full"
              :directives="{ 'v-style': { objectPosition } }"
              fit="cover"
              loop
            />
            <cms-shared-hot-zones v-if="hasMediaHotZones(mediaList[0])" :media="mediaList[0]" />
          </div>
        </div>
      </div>
      <base-link
        v-if="mediaLink && !hasHotZones"
        aria-hidden="true"
        class="absolute-0"
        :data-cta="mediaLink.delocalizedTracking"
        tabindex="-1"
        :target="mediaLink.targetAttribute"
        :to="mediaLink.url"
        @click="handlePromotionClick('img')"
      />
    </div>
    <div
      v-else
      class="relative flex shrink-0"
      :class="[
        mediaSize[imageWidth],
        {
          '<md:flex-1 <md:min-h-0 <md:overflow-hidden': usesPercentHeightSmMd,
          'lg:h-full lg:min-h-0 lg:overflow-hidden': usesPercentHeightLg,
        },
      ]"
    >
      <cms-base-caption v-if="hasCaption" v-bind="captionProps" />
      <div v-if="responsiveMediaList.length > 0" class="grid cols-2 w-full md:flex" :class="roundedCornersClasses">
        <div v-for="(mediaObject, i) in responsiveMediaList" :key="i" class="relative" :class="getMediaClasses(i)">
          <base-picture
            v-if="mediaObject.images"
            v-bind="mediaObject.images"
            class="full"
            :fetchpriority
            fit="cover"
            :lazy
            :position="objectPosition"
            :props-img="{ class: 'full' }"
          />
          <vf-video
            v-else-if="mediaObject.video"
            v-bind="mediaObject.video"
            ref="videoRefs"
            :autoplay="!lazy"
            class="w-full"
            :directives="{ 'v-style': { objectPosition } }"
            fit="cover"
            loop
          />
          <cms-shared-hot-zones v-if="hasMediaHotZones(mediaList[i])" :media="mediaList[i]" />
        </div>
      </div>
      <base-link
        v-if="mediaLink && !hasHotZones"
        aria-hidden="true"
        class="absolute-0"
        :data-cta="mediaLink.delocalizedTracking"
        tabindex="-1"
        :target="mediaLink.targetAttribute"
        :to="mediaLink.url"
        @click="handlePromotionClick('img')"
      />
    </div>
    <div
      v-style:bg="splitLayoutAlignmentLarge.startsWith('1') ? backgroundColor : null"
      class="w-full f-col"
      :class="[
        contentAlignment,
        {
          '<md:shrink-0': usesPercentHeightSmMd,
          'md:min-h-0 lg:min-h-0': usesPercentHeightLg,
        },
      ]"
    >
      <div v-if="splitLayoutAlignmentLarge.startsWith('3')" class="grid cols-2 <md:hidden" :class="roundedCornersClasses">
        <div v-for="(mediaObject, i) in responsiveMediaList.slice(-2)" :key="i" class="relative">
          <base-picture
            v-if="mediaObject.images"
            v-bind="mediaObject.images"
            class="w-full"
            :fetchpriority
            fit="cover"
            :lazy
            :position="objectPosition"
          />
          <vf-video
            v-else-if="mediaObject.video"
            v-bind="mediaObject.video"
            ref="videoRefs"
            :autoplay="!lazy"
            class="w-full"
            :directives="{ 'v-style': { objectPosition } }"
            fit="cover"
            loop
          />
          <cms-shared-hot-zones v-if="hasMediaHotZones(mediaList.slice(-2)[i])" :media="mediaList.slice(-2)[i]" />
        </div>
      </div>
      <div
        class="w-full f-col grow px-4 py-6 space-y-2 lg:px-6 lg:space-y-4"
        :class="[
          textAlignment,
          {
            'order-first': splitLayoutAlignmentLarge === '3-top-left' || splitLayoutAlignmentLarge === '3-top-right',
          },
          overlayAlignmentClasses[splitOverlayAlignmentLarge]]"
      >
        <cms-base-parallax-layer
          class="f-col gap-y-2 lg:gap-y-4"
          :class="overlayWidth ? contentSize[overlayWidth] : ''"
          :config="parallaxLayer"
        >
          <cms-base-eyebrow
            v-if="eyebrowText || eyebrowTextHtml || eyebrowLogoResponsive"
            :eyebrow-logo-responsive
            :eyebrow-logo-width-responsive
            :eyebrow-text
            :eyebrow-text-color="eyebrowTextColor || subtitleColor"
            :eyebrow-text-html
            :eyebrow-text-style
          />
          <p v-if="overline" v-style:c="overlineColor" :class="overlineStyle" data-test-id="cms-banner-overline">
            {{ overline }}
          </p>
          <component
            :is="titleTag"
            v-if="title"
            v-style:c="titleColor"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-banner-title"
          >
            <base-link
              v-if="mediaLink"
              :data-cta="mediaLink.delocalizedTracking"
              :target="mediaLink.targetAttribute"
              :to="mediaLink.url"
              @click="handlePromotionClick('title')"
            >
              <cms-plain-text :content field-name="title" />
            </base-link>
            <template v-else>
              <cms-plain-text :content field-name="title" />
            </template>
          </component>
          <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle" data-test-id="cms-banner-text">
            <cms-plain-text :content field-name="subtitle" />
          </p>
          <cms-rich-text
            v-if="richText"
            v-style:c="richTextColor || subtitleColor"
            :content="{ richText }"
            data-test-id="cms-banner-body"
          />
          <cms-shared-cta-group
            v-if="linkType !== 'No-CTA' && targets.length"
            :class="ctaClass"
            :cta-type
            equal-size-small-class="<md:grid <md:cols-$cols <md:cols-1 <md:auto-rows-fr"
            :equal-target-size
            layout="flex-col gap-4 i-flex md:flex-row md:wrap lg:gap-6 not-first:pt-4 <md:o:w-full "
            :multilink-style
            :multilink-title
            :size
            :targets
            :text-link-style="getTextLinkStyles(content)"
            @click="handlePromotionClick(`cta${$event + 1}`)"
          />
          <div>
            <cms-rich-text
              v-if="richTextBelowTargets"
              v-style:c="richTextBelowTargetsColor || subtitleColor"
              :content="{ richText: richTextBelowTargets }"
            />
          </div>
        </cms-base-parallax-layer>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type {
  BannerSplitAlignmentLarge,
  BannerSplitAlignmentSmall,
  BannerSplitContent,
} from '#types/components/cms/banner-split'
import type { ContentPosition } from '#types/components/cms/positions'
import type { TextAlignment } from '#types/components/cms/text-alignment'
import type { ResponsiveMedia, SplitMediaWidth } from '#types/content'
import { DynamicFieldsKey } from '#content/contexts/dynamic-fields'
import { SectionContextKey } from '#content/contexts/section'

const { content, firstElement, index, isPartOfCollection, parentHeadingLevel } = defineProps<{
  content: BannerSplitContent
  firstElement?: boolean
  index?: number
  isPartOfCollection?: boolean
  parentHeadingLevel?: number
}>()

const {
  splitLayoutAlignmentLarge,
  splitOverlayAlignmentLarge,
  alignmentSmall,
  backgroundColor,
  componentPercentHeight,
  countdown,
  ctaType,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowLogoWidthResponsive,
  eyebrowText,
  eyebrowTextHtml,
  eyebrowTextColor,
  eyebrowTextStyle,
  hideTitle,
  imageWidth = '60',
  linkType,
  mediaList = [],
  mediaTarget,
  multilinkStyle,
  multilinkTitle,
  name,
  overlayWidth,
  overline,
  overlineColor,
  overlineStyle,
  parallax,
  promotionTracking,
  richText,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  richTextColor,
  subtitle,
  subtitleColor,
  subtitleStyle,
  targets = [],
  textAlignmentLarge,
  textAlignmentSmall,
  title,
  titleColor,
  titleH1,
  titleStyle,
} = content

countdown && provide(DynamicFieldsKey, { countdown })

const cms = useAppConfig().components.cms
const { size } = cms.sharedButton
const { componentCorners } = cms.section
const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const videoRefs = ref<HTMLVideoElement[]>([])
const mediaLink = mediaTarget || targets[0]
const responsiveMediaList = mediaList.map((m) => getResponsiveMedia(m))
const responsiveMedia = getResponsiveMedia(mediaList[0])
const aspectRatio = responsiveMedia?.aspectRatio || {}

const hasMediaHotZones = (media?: ResponsiveMedia) => Object.values(media || {}).some((m) => m?.enableImageMap)
const hasHotZones = mediaList.some(hasMediaHotZones)

const smallImageLayout = getAlignment(alignmentSmall) || 0
const imageLayout = getAlignment(splitLayoutAlignmentLarge) || 0

const contentPane = Object.keys(aspectRatio).reduce((acc, cur) => {
  acc[cur] = `calc(1 / ${aspectRatio[cur]} * 100%)`
  return acc
}, {})

const parallaxOffset = parallax?.config.innerOffset
const parallaxLayer = useParallaxTextLayer(parallax?.config.textLayer)
const parallaxRef = useTemplateRef('parallaxRef')
const { config: parallaxConfig, translate: parallaxTranslate } = getOffsetParallaxConfig(parallaxOffset, {
  0: 0,
  1: 50,
  2: 70,
  3: 85,
  4: 100,
  5: 125,
})

if (splitLayoutAlignmentLarge.startsWith('1') && parallaxConfig) {
  useParallaxController(parallaxRef, parallaxConfig)
}

const contentAlignmentLarge: Record<BannerSplitAlignmentLarge, string> = {
  'left-left': 'md:text-left md:order-first',
  'left-right': 'md:text-left md:order-last',
  'center-left': 'md:text-center md:order-first',
  'center-right': 'md:text-center md:order-last',
  'right-left': 'md:text-right md:order-first',
  'right-right': 'md:text-right md:order-last',
  '1-left': 'md:order-first',
  '1-right': 'md:order-last',
  '2-left': 'md:order-first',
  '2-right': 'md:order-last',
  '3-bottom-left': 'md:order-first',
  '3-bottom-right': 'md:order-last',
  '3-top-left': 'md:order-first',
  '3-top-right': 'md:order-last',
}

const contentAlignmentSmall: Partial<Record<BannerSplitAlignmentSmall, string>> = {
  left: '<md:text-left',
  center: '<md:text-center',
  right: '<md:text-right'
}

const ctaAlignmentLarge: Partial<Record<BannerSplitAlignmentLarge | TextAlignment, string>> = {
  'left-left': 'md:justify-start',
  'left-right': 'md:justify-start',
  'center-left': 'md:justify-center',
  'center-right': 'md:justify-center',
  'right-left': 'md:justify-end',
  'right-right': 'md:justify-end',
  'left': 'md:justify-start',
  'center': 'md:justify-center',
  'right': 'md:justify-end'
}

const ctaAlignmentSmall: Partial<Record<TextAlignment, string>> = {
  left: '<md:items-start',
  center: '<md:items-center',
  right: '<md:items-end'
}

const textAlignmentSmallClasses: Record<TextAlignment, string> = {
  left: '<md:text-left',
  center: '<md:text-center',
  right: '<md:text-right'
}

const textAlignmentLargeClasses: Record<TextAlignment, string> = {
  left: 'md:text-left',
  center: 'md:text-center',
  right: 'md:text-right'
}

const overlayAlignmentClasses: Record<ContentPosition, string> = {
  'top-left': 'md:items-start',
  'top-center': 'md:items-center',
  'top-right': 'md:items-end',
  'middle-left': 'md:items-start md:justify-center',
  'middle-center': 'md:center',
  'middle-right': 'md:justify-center md:items-end',
  'bottom-left': 'md:items-start md:justify-end',
  'bottom-center': 'md:items-center md:justify-end',
  'bottom-right': 'md:items-end md:justify-end',
}

const { hasCaption, captionProps } = getCaptionConfig(content)

const mediaSize: Record<SplitMediaWidth, string> = {
  60: 'md:w-3/5',
  50: 'md:w-1/2',
  40: 'md:w-2/5'
}

const getMediaClasses = (i: number) => ({
  '<md:col-span-2': i === 0 && [3, 1, 0].includes(smallImageLayout),
  '<md:hidden': smallImageLayout && i > smallImageLayout - 1,
  'md:hidden': (imageLayout && (i > imageLayout - 1)) || (imageLayout === 3 && i > 0),
})

const contentAlignment = [
  contentAlignmentLarge[splitLayoutAlignmentLarge],
  contentAlignmentSmall[alignmentSmall],
]

const textAlignment = [
  textAlignmentSmallClasses[textAlignmentSmall],
  textAlignmentLargeClasses[textAlignmentLarge]
]

const ctaClass = [
  ctaAlignmentLarge[splitLayoutAlignmentLarge],
  ctaAlignmentSmall[alignmentSmall],
  ctaAlignmentLarge[textAlignmentLarge],
  ctaAlignmentSmall[textAlignmentSmall]
]

const contentSize = {
  xs: 'w-10/12 md:w-1/3',
  sm: 'w-10/12 md:w-1/2',
  md: 'w-10/12 md:w-2/3',
  lg: 'w-full md:w-11/12'
}

const titleTag = computed(() => titleH1
  ? 'h1'
  : isPartOfCollection
    ? parentHeadingLevel ? `h${parentHeadingLevel + 1}` : firstElement && index === 0 ? 'h1' : 'h2'
    : firstElement ? 'h1' : 'h2')

const roundedCornersClasses = getRoundedCorners(content, componentCorners, { enableWhen: {
  sm: smallImageLayout === 1,
  lg: imageLayout === 1
} })

const percentHeightHeaderOffsetPx = useHydrationSafeHeaderOffsetPx()

const usesPercentHeightSmMd = hasPercentHeightAtBreakpoint(componentPercentHeight?.small)
const usesPercentHeightLg = hasPercentHeightAtBreakpoint(componentPercentHeight?.large)
const anchorSmall = componentPercentHeight?.anchorSmall
const anchorLarge = componentPercentHeight?.anchorLarge

const percentHeightStyle = computed(() =>
  getPercentHeightCssVars(componentPercentHeight, percentHeightHeaderOffsetPx.value),
)

const objectPosition = getResponsiveObjectPosition(anchorSmall, anchorLarge)

onMounted(() => videoRefs.value.forEach(({ play }) => play()))
</script>
